import React, { useEffect, useState } from 'react';
import { Lock, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import { useFeaturePermissions } from '../globalfeatures';
import { ticketService, isTicketApiSuccess } from '../../api/ticketService';
import { extractErrorMessage } from '../../utils/errorHandler';

/** Must match TicketConfidentialityAccessService.FEATURE_TICKET_CONFIDENTIALITY_LEVEL (can_view / can_update). */
export const FEATURE_UPDATE_TICKET_CONFIDENTIALITY_LEVEL = 'update_ticket_confidentiality_level';

export const CONFIDENTIALITY_LEVEL_OPTIONS = ['NORMAL', 'RESTRICTED', 'CONFIDENTIAL'];

export function normalizeConfidentialityLevel(value) {
  const raw = String(value || 'NORMAL').trim().toUpperCase();
  return CONFIDENTIALITY_LEVEL_OPTIONS.includes(raw) ? raw : 'NORMAL';
}

/** Visible label for RESTRICTED / CONFIDENTIAL tickets (hidden for NORMAL). */
export function ConfidentialityLevelBadge({ level, className = '' }) {
  const normalized = normalizeConfidentialityLevel(level);
  if (normalized === 'NORMAL') {
    return null;
  }
  if (normalized === 'CONFIDENTIAL') {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 border border-red-200 dark:bg-red-950 dark:text-red-200 dark:border-red-800 ${className}`}
        title="Confidential inquiry — handle per restricted access policy"
      >
        <Shield className="h-3 w-3 shrink-0" aria-hidden />
        Confidential inquiry
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-900 border border-amber-300 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-700 ${className}`}
      title="Restricted inquiry — handle per restricted access policy"
    >
      <Lock className="h-3 w-3 shrink-0" aria-hidden />
      Restricted inquiry
    </span>
  );
}

/**
 * Queue-only confidentiality selector (Unassigned / HDO pending). Hidden without master-feature can_update.
 */
export default function QueueConfidentialityLevelControl({
  ticketId,
  initialLevel,
  disabled = false,
  onSaved,
}) {
  const perms = useFeaturePermissions(FEATURE_UPDATE_TICKET_CONFIDENTIALITY_LEVEL);
  const [draftLevel, setDraftLevel] = useState(normalizeConfidentialityLevel(initialLevel));
  const [persistedLevel, setPersistedLevel] = useState(normalizeConfidentialityLevel(initialLevel));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const next = normalizeConfidentialityLevel(initialLevel);
    setDraftLevel(next);
    setPersistedLevel(next);
  }, [ticketId, initialLevel]);

  if (!perms.canUpdate || ticketId == null) {
    return null;
  }

  const handleSave = async () => {
    if (draftLevel === persistedLevel) {
      return;
    }
    setSaving(true);
    try {
      const response = await ticketService.updateTicketConfidentialityLevel(ticketId, draftLevel);
      if (!isTicketApiSuccess(response)) {
        toast.error(extractErrorMessage(response, 'Failed to update confidentiality level'));
        return;
      }
      const updated = response?.data?.data ?? response?.data ?? response;
      const saved = normalizeConfidentialityLevel(updated?.confidentialityLevel ?? draftLevel);
      setPersistedLevel(saved);
      setDraftLevel(saved);
      toast.success(
        saved === 'NORMAL'
          ? 'Confidentiality level updated'
          : 'Confidentiality level updated. This ticket may leave the queue for users without access.'
      );
      onSaved?.({
        ticketId,
        confidentialityLevel: saved,
        /** Unassigned/HDO: ticket hidden from general queue when elevated to R/C. */
        removedFromQueue: saved !== 'NORMAL',
        /** D2.7 entitled list: ticket drops when returned to NORMAL. */
        leftRestrictedConfidentialList: saved === 'NORMAL',
      });
    } catch (error) {
      console.error('Confidentiality level update failed:', error);
      toast.error(extractErrorMessage(error, 'Failed to update confidentiality level'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2 rounded-md border border-gray-200 dark:border-gray-600 p-3">
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
        Confidentiality level
      </label>
      <select
        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50"
        value={draftLevel}
        onChange={(e) => setDraftLevel(e.target.value)}
        disabled={disabled || saving}
      >
        {CONFIDENTIALITY_LEVEL_OPTIONS.map((level) => (
          <option key={level} value={level}>
            {level.charAt(0) + level.slice(1).toLowerCase()}
          </option>
        ))}
      </select>
      {draftLevel === persistedLevel && (
        <p className="text-xs text-muted-foreground">
          Select a different confidentiality level to enable Save.
        </p>
      )}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={disabled || saving || draftLevel === persistedLevel}
          className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save confidentiality'}
        </button>
      </div>
    </div>
  );
}
